import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../redux/store';

import { INote, IWidgetData } from '../../../../../common/services/http/analysisContentService';
import { IScenario } from '../../../types/IScenario';
import { ISetup } from '../../../types/ISetup';
import { ICurrency } from '../../../types/ICurrency';
import { IWidget } from '../../../../../common/services/http/types/IWidget';
import { AnalysisContentWidgetModel } from '../../../../../models/AnalysisContentWidgetModel';
import { IMeasure } from '../../../types/IMeasure';
import { IStaticProperty } from '../../../../../models/types/IStaticProperty';
import { AnalysisContentWidgetDataModel } from '../../../../../models/AnalysisContentWidgetDataModel';

const useGetMapData = (
  setup: ISetup,
  widget: AnalysisContentWidgetModel | IWidget,
  measures: IMeasure[],
  notes: INote[],
  boardId: number,
  staticProperties: IStaticProperty[],
  scenarios: IScenario[],
  selectedObject: any,
  selectedPeriod: any,
  boardSelectedCurrency: any,
  investorObjectId: any,
) => {
  const [lat, setLat] = useState<number>(35);
  const [long, setLong] = useState<number>(-10);
  const [zoom, setZoom] = useState<number>(3);
  const [values, setValues] = useState<any[]>(null);
  const [currencies, setCurrencies] = useState<ICurrency[]>([]);
  const [selectedCurrency, setSelectedCurrency] = useState<ICurrency[]>([]);
  const [selectData, setSelectData] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [measureLayersState, setMeasureLayerState] = useState<any[]>([]);
  const [selectedMeasureState, setSelectedMeasureState] = useState<any>(null);

  const widgetDataRef = useRef(null);
  const investor = useSelector((state: RootState) => state.investor.investor);
  const { investorId } = useParams();

  let measureLayers = [];
  let selectedMeasure = null;

  const tileSubdomains = ['a', 'b', 'c'];
  const tileUrl = (e) =>
    `https://${e.subdomain}.tile.openstreetmap.org/${e.zoom}/${e.x}/${e.y}.png`;
  const attribution =
    '&copy; <a href="https://osm.org/copyright">OpenStreetMap contributors</a>';

  const bubbleStyle = {
    fill: {
      color: '#00f',
      opacity: 0.4,
    },
  };

  const staticPropertiesForFetch = () => {
    let propertiesForFetch = [];
    setup.definition && setup.definition.forEach((def) => {
      if (
        def.templatePropertyId &&
        !propertiesForFetch.find((item) => {
          return item.templatePropertyId === def.templatePropertyId;
        })
      )
        propertiesForFetch.push({ templatePropertyId: def.templatePropertyId });
    });
    return propertiesForFetch;
  };

  const defineLayers = () => {
    let dropdownData = [];
    widget.setup.measures.forEach((measure) => {
      let meas = measures.find((item) => {
        return item.id === measure.measureId;
      });
      if (meas) {
        measureLayers.push({
          measureId: measure.measureId,
          name: meas.name,
          amountsIn: measure.amountsIn,
          scale: measure.scale,
          viewCashflows: measure.viewCashflows,
          data: [],
        });

        dropdownData.push({ measureId: measure.measureId, name: meas.name });
      }
    });
    setSelectData(dropdownData);

    if (dropdownData.length > 0)
      selectedMeasure = dropdownData[0];
  };

  const onSelectChange = (e) => {
    setSelectedMeasureState(e.value);
    let layer = measureLayersState.find((item) => {
      return item.measureId === e.value.measureId;
    });
    setValues(layer.data);
  };

  const handleCurrencyChange = (e) => {
    setSelectedCurrency(e.value);
  };

  const clearLayerData = () => {
    measureLayers.forEach((layer) => {
      layer.data.splice(0);
    });
  };

  const getLayer = (measureId) => {
    return measureLayers.find((item) => {
      return item.measureId === measureId;
    });
  };

  const assembleLayerData = (data) => {
    clearLayerData();

    let nodes = selectedObject.nodes;

    data.forEach((value) => {
      let layer = getLayer(value.measureId);
      if (layer) {
        let node = nodes.find((item) => {
          return item.id === value.nodeId;
        });
        if (node)
          layer.data.push({
            name: node.name,
            measureName: layer.name,
            scale: layer.scale,
            location: [node.latitude, node.longitude],
            value: value.amount / layer.amountsIn,
            viewCashflows: layer.viewCashflows,
            nodeId: value.nodeId,
            valueId: value.id,
            scenarioId: value.scenarioId,
            measureId: value.measureId,
          });
      }
    });
  };

  const handleFindSelection = (currency, arrayToFind) => {
    if (arrayToFind.find((curr) => curr.currency === currency.currency)) {
      return currency;
    } else {
      return false;
    }
  };

  useEffect(() => {
    widgetDataRef.current = new AnalysisContentWidgetDataModel(widget, measures, investorId ? parseInt(investorId) : parseInt(investorObjectId), false, staticPropertiesForFetch());
    widgetDataRef.current.defineNodes(selectedObject);
    setLat(parseFloat(widget.setup.latitude));
    setLong(parseFloat(widget.setup.longitude));
    setZoom(parseInt(widget.setup.zoom));
  }, [investor]);

  useEffect(() => {
    if (boardSelectedCurrency) {
      let currency = handleFindSelection(boardSelectedCurrency, widgetDataRef.current.generateCurrencies(scenarios));
      if (currency) {
        setSelectedCurrency(currency);
      }
    }
  }, [boardSelectedCurrency]);

  useEffect(() => {
    //@ts-ignore
    if (selectedCurrency.currency) {
      widgetDataRef.current.defineNodes(selectedObject);
      setCurrencies(widgetDataRef.current.generateCurrencies(scenarios));

      if (investorId) {
        widgetDataRef.current.setInvestorId(investorId);
      } else {
        widgetDataRef.current.setInvestorId(investorObjectId);
      }

      defineLayers();
      //@ts-ignore
      widgetDataRef.current.fetchNodeData(selectedPeriod, selectedCurrency.currency).then((res: IWidgetData[]) => {
        assembleLayerData(res);

        setMeasureLayerState(measureLayers);

        let selectedData = [];
        if (selectedMeasureState) {
          let layer = getLayer(selectedMeasureState.measureId);
          if (layer)
            selectedData = layer.data;
        } else if (selectedMeasure) {
          let layer = getLayer(selectedMeasure.measureId);
          if (layer)
            selectedData = layer.data;
        }

        setValues(selectedData);
        setLoading(false);
      });
    }
  }, [selectedObject, selectedPeriod, selectedCurrency]);

  return {
    lat,
    long,
    zoom,
    loading,
    currencies,
    handleCurrencyChange,
    selectedCurrency,
    values,
    selectData,
    onSelectChange,
    tileSubdomains,
    tileUrl,
    attribution,
    bubbleStyle,
  };
};

export default useGetMapData;
